import React, { useEffect, useMemo, useState } from "react";
import "./ChatEtiquetaManager.css";

import {
    ListarMetaWhatsAppEtiquetas,
    ListarEtiquetasDoContato,
    IncluirMetaWhatsAppEtiqueta,
    AtualizarMetaWhatsAppEtiqueta,
    RemoverMetaWhatsAppEtiqueta,
    SincronizarEtiquetasDoContato
} from "../../../Services/MetaWhatsAppEtiqueta";

const MAX_ETIQUETA_LEN = 20;

export async function ConsultarEtiquetasDoContato({ usuarioId, whatsAppId }) {
    if (!usuarioId || usuarioId <= 0) {
        return { success: false, etiquetaIds: [], message: "Usuário não informado." };
    }

    if (!whatsAppId || !String(whatsAppId).trim()) {
        return { success: false, etiquetaIds: [], message: "WhatsAppId não informado." };
    }

    const resp = await ListarEtiquetasDoContato(usuarioId, String(whatsAppId).trim());

    if (!resp?.success) {
        return {
            success: false,
            etiquetaIds: [],
            message: resp?.message || "Erro ao listar etiquetas do contato."
        };
    }

    return {
        success: true,
        etiquetaIds: Array.isArray(resp.etiquetaIds) ? resp.etiquetaIds : [],
        message: ""
    };
}

export default function ChatEtiquetaManager({
    aberto,
    onFechar,
    usuarioId,
    whatsAppId,
    onSalvoSucesso,

    // ✅ NOVO: modo consulta/filtro (não mexe em conversa)
    modoConsultaFiltro = false,
    selecionadasInicial = [], // ids já selecionados (ex: filtro atual)
    onAplicarFiltro // (idsSelecionados) => void
}) {
    const [loading, setLoading] = useState(false);
    const [salvandoVinculos, setSalvandoVinculos] = useState(false);

    const [etiquetas, setEtiquetas] = useState([]);
    const [selecionadasSet, setSelecionadasSet] = useState(new Set());

    const [filtro, setFiltro] = useState("");

    // modo: lista/vinculo vs form (criar/editar)
    const [modo, setModo] = useState("lista"); // "lista" | "form"
    const [formLoading, setFormLoading] = useState(false);
    const [editandoId, setEditandoId] = useState(0);
    const [nomeEtiqueta, setNomeEtiqueta] = useState("");

    const selecionadasCount = selecionadasSet?.size || 0;

    const resetForm = () => {
        setEditandoId(0);
        setNomeEtiqueta("");
        setFormLoading(false);
    };

    const voltarParaLista = () => {
        resetForm();
        setModo("lista");
    };

    const abrirCriacao = () => {
        if (modoConsultaFiltro) return; // ✅ no modo filtro não cria
        resetForm();
        setModo("form");
    };

    const abrirEdicao = (item) => {
        if (modoConsultaFiltro) return; // ✅ no modo filtro não edita
        setEditandoId(item?.metaEtiquetasConversasId || 0);
        setNomeEtiqueta(item?.nomeEtiqueta || "");
        setModo("form");
    };

    const carregarTudo = async () => {
        if (!usuarioId || usuarioId <= 0) {
            alert("Usuário não informado.");
            return;
        }

        setLoading(true);

        // ✅ Sempre precisa da lista de etiquetas
        const rEtiquetas = await ListarMetaWhatsAppEtiquetas(usuarioId);

        if (!rEtiquetas.success) {
            setLoading(false);
            alert(rEtiquetas.message || "Erro ao listar etiquetas.");
            return;
        }

        const list = (rEtiquetas.etiquetas || []).slice();
        list.sort((a, b) =>
            String(a.nomeEtiqueta || "").localeCompare(String(b.nomeEtiqueta || ""), "pt-BR")
        );

        setEtiquetas(list);

        // ✅ Se for modo filtro, seleção vem da prop (sem consultar contato)
        if (modoConsultaFiltro) {
            const ids = Array.isArray(selecionadasInicial) ? selecionadasInicial : [];
            setSelecionadasSet(new Set(ids));
            setFiltro("");
            setModo("lista");
            resetForm();
            setLoading(false);
            return;
        }

        // ✅ modo normal: precisa do whatsAppId e consulta vínculos
        if (!whatsAppId || !String(whatsAppId).trim()) {
            setLoading(false);
            alert("WhatsAppId não informado.");
            return;
        }

        const rContato = await ConsultarEtiquetasDoContato({
            usuarioId,
            whatsAppId: String(whatsAppId).trim()
        });

        setLoading(false);

        if (!rContato.success) {
            alert(rContato.message || "Erro ao listar etiquetas do contato.");
            return;
        }

        const ids = Array.isArray(rContato.etiquetaIds) ? rContato.etiquetaIds : [];
        setSelecionadasSet(new Set(ids));

        setFiltro("");
        setModo("lista");
        resetForm();
    };

    // ✅ Carrega ao abrir
    useEffect(() => {
        if (!aberto) return;
        carregarTudo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aberto]);

    // ✅ Se selecionadasInicial mudar com o modal aberto (ex: você abriu de novo com filtro diferente)
    useEffect(() => {
        if (!aberto) return;
        if (!modoConsultaFiltro) return;

        const ids = Array.isArray(selecionadasInicial) ? selecionadasInicial : [];
        setSelecionadasSet(new Set(ids));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selecionadasInicial, aberto, modoConsultaFiltro]);

    const etiquetasFiltradas = useMemo(() => {
        const term = String(filtro || "").trim().toLowerCase();
        if (!term) return etiquetas;

        return (etiquetas || []).filter((e) =>
            String(e?.nomeEtiqueta || "").toLowerCase().includes(term)
        );
    }, [etiquetas, filtro]);

    const toggleEtiqueta = (id) => {
        setSelecionadasSet((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // ✅ modo normal: salvar vínculos
    const salvarVinculos = async () => {
        if (!usuarioId || usuarioId <= 0) {
            alert("Usuário não informado.");
            return;
        }

        if (!whatsAppId || !String(whatsAppId).trim()) {
            alert("WhatsAppId não informado.");
            return;
        }

        setSalvandoVinculos(true);

        const ids = Array.from(selecionadasSet || []);
        const resp = await SincronizarEtiquetasDoContato(usuarioId, String(whatsAppId).trim(), ids);

        setSalvandoVinculos(false);

        if (!resp.success) {
            alert(resp.message || "Erro ao salvar vínculos de etiquetas.");
            return;
        }

        if (onSalvoSucesso) {
            onSalvoSucesso({ etiquetaIds: ids });
        }

        onFechar();
    };

    // ✅ modo filtro: aplicar seleção como filtro
    const aplicarFiltro = () => {
        const ids = Array.from(selecionadasSet || []);
        if (onAplicarFiltro) {
            onAplicarFiltro(ids);
        }
        onFechar();
    };

    const limparSelecao = () => {
        setSelecionadasSet(new Set());
    };

    // ======= CRUD (somente modo normal) =======
    const validarNome = (nome) => {
        const n = String(nome || "").trim();
        if (!n) return "Nome da etiqueta não informado.";
        if (n.length > MAX_ETIQUETA_LEN) return `Nome da etiqueta deve ter no máximo ${MAX_ETIQUETA_LEN} caracteres.`;
        return "";
    };

    const salvarEtiqueta = async () => {
        const erro = validarNome(nomeEtiqueta);
        if (erro) {
            alert(erro);
            return;
        }

        if (!usuarioId || usuarioId <= 0) {
            alert("Usuário não informado.");
            return;
        }

        const nomeTrim = String(nomeEtiqueta || "").trim().toLowerCase();
        const duplicada = (etiquetas || []).some((e) => {
            const id = e?.metaEtiquetasConversasId || 0;
            if (editandoId > 0 && id === editandoId) return false;
            return String(e?.nomeEtiqueta || "").trim().toLowerCase() === nomeTrim;
        });

        if (duplicada) {
            alert("Já existe uma etiqueta com esse nome.");
            return;
        }

        setFormLoading(true);

        if (!editandoId || editandoId <= 0) {
            const resp = await IncluirMetaWhatsAppEtiqueta({
                usuarioId,
                nomeEtiqueta: String(nomeEtiqueta || "").trim()
            });

            setFormLoading(false);

            if (!resp.success) {
                alert(resp.message || "Erro ao incluir etiqueta.");
                return;
            }

            const novoId = resp.etiquetaId || 0;
            if (novoId > 0) {
                setEtiquetas((prev) => {
                    const next = [
                        ...(prev || []),
                        {
                            metaEtiquetasConversasId: novoId,
                            usuarioId,
                            nomeEtiqueta: String(nomeEtiqueta || "").trim()
                        }
                    ];
                    next.sort((a, b) => String(a.nomeEtiqueta || "").localeCompare(String(b.nomeEtiqueta || ""), "pt-BR"));
                    return next;
                });
            }

            voltarParaLista();
            return;
        }

        const resp = await AtualizarMetaWhatsAppEtiqueta({
            usuarioId,
            metaEtiquetasConversasId: editandoId,
            nomeEtiqueta: String(nomeEtiqueta || "").trim()
        });

        setFormLoading(false);

        if (!resp.success || !resp.ok) {
            alert(resp.message || "Erro ao atualizar etiqueta.");
            return;
        }

        setEtiquetas((prev) => {
            const next = (prev || []).map((e) => {
                if ((e?.metaEtiquetasConversasId || 0) !== editandoId) return e;
                return { ...e, nomeEtiqueta: String(nomeEtiqueta || "").trim() };
            });
            next.sort((a, b) => String(a.nomeEtiqueta || "").localeCompare(String(b.nomeEtiqueta || ""), "pt-BR"));
            return next;
        });

        voltarParaLista();
    };

    const excluirEtiqueta = async () => {
        if (!usuarioId || usuarioId <= 0) {
            alert("Usuário não informado.");
            return;
        }

        if (!editandoId || editandoId <= 0) return;

        const ok = window.confirm(
            "Excluir esta etiqueta?\n\nObservação: os vínculos com contatos serão removidos também."
        );
        if (!ok) return;

        setFormLoading(true);

        const resp = await RemoverMetaWhatsAppEtiqueta(usuarioId, editandoId);

        setFormLoading(false);

        if (!resp.success || !resp.ok) {
            alert(resp.message || "Erro ao remover etiqueta.");
            return;
        }

        setEtiquetas((prev) => (prev || []).filter((e) => (e?.metaEtiquetasConversasId || 0) !== editandoId));

        setSelecionadasSet((prev) => {
            const next = new Set(prev);
            next.delete(editandoId);
            return next;
        });

        voltarParaLista();
    };

    if (!aberto) return null;

    const titulo = modo === "form"
        ? (editandoId > 0 ? "Editar Etiqueta" : "Nova Etiqueta")
        : (modoConsultaFiltro ? "Filtrar por Etiquetas" : "Etiquetas");

    return (
        <div className="chat-etiqueta-modal-overlay">
            <div className="chat-etiqueta-modal">
                {/* HEADER */}
                <div className="chat-etiqueta-modal-header">
                    <div className="chat-etiqueta-title">
                        {titulo}
                        {modo === "lista" && (
                            <span className="chat-etiqueta-subtitle">
                                {" "}
                                • {selecionadasCount} selecionada{selecionadasCount === 1 ? "" : "s"}
                            </span>
                        )}
                    </div>

                    <button
                        type="button"
                        className="chat-etiqueta-close"
                        onClick={onFechar}
                        disabled={salvandoVinculos || formLoading}
                        aria-label="Fechar"
                        title="Fechar"
                    >
                        ✕
                    </button>
                </div>

                {/* BODY */}
                <div className="chat-etiqueta-modal-body">
                    {loading ? (
                        <div>Carregando etiquetas...</div>
                    ) : modo === "form" ? (
                        <>
                            <label className="chat-etiqueta-label">Nome</label>

                            <input
                                type="text"
                                className="chat-etiqueta-input"
                                value={nomeEtiqueta}
                                maxLength={MAX_ETIQUETA_LEN}
                                placeholder="Ex: VIP, Cobrança, Renovação..."
                                onChange={(e) => setNomeEtiqueta(e.target.value)}
                                disabled={formLoading}
                            />

                            <div className="chat-etiqueta-helper">
                                {String(nomeEtiqueta || "").trim().length}/{MAX_ETIQUETA_LEN}
                            </div>

                            <div className="chat-etiqueta-form-actions">
                                <button
                                    type="button"
                                    className="chat-etiqueta-btn-secondary"
                                    onClick={voltarParaLista}
                                    disabled={formLoading}
                                >
                                    Cancelar
                                </button>

                                {editandoId > 0 && (
                                    <button
                                        type="button"
                                        className="chat-etiqueta-btn-danger"
                                        onClick={excluirEtiqueta}
                                        disabled={formLoading}
                                        title="Excluir etiqueta (remove vínculos também)"
                                    >
                                        Excluir
                                    </button>
                                )}

                                <button
                                    type="button"
                                    className="chat-etiqueta-btn-primary"
                                    onClick={salvarEtiqueta}
                                    disabled={formLoading}
                                >
                                    {formLoading ? "Salvando..." : "Salvar"}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="chat-etiqueta-toolbar">
                                <input
                                    type="text"
                                    className="chat-etiqueta-search"
                                    value={filtro}
                                    onChange={(e) => setFiltro(e.target.value)}
                                    placeholder="Pesquisar etiqueta..."
                                    autoComplete="off"
                                    disabled={salvandoVinculos}
                                />

                                {/* ✅ botão nova etiqueta só no modo normal */}
                                {!modoConsultaFiltro && (
                                    <button
                                        type="button"
                                        className="chat-etiqueta-btn-primary"
                                        onClick={abrirCriacao}
                                        disabled={salvandoVinculos}
                                        title="Criar nova etiqueta"
                                    >
                                        + Nova Etiqueta
                                    </button>
                                )}
                            </div>

                            {etiquetasFiltradas.length === 0 ? (
                                <div className="chat-etiqueta-empty">
                                    Nenhuma etiqueta encontrada.
                                </div>
                            ) : (
                                <div className="chat-etiqueta-list">
                                    {etiquetasFiltradas.map((e) => {
                                        const id = e?.metaEtiquetasConversasId;
                                        const checked = selecionadasSet.has(id);

                                        return (
                                            <div key={id} className="chat-etiqueta-item">
                                                <label className="chat-etiqueta-item-left">
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleEtiqueta(id)}
                                                        disabled={salvandoVinculos}
                                                    />
                                                    <span className="chat-etiqueta-item-name">
                                                        {e?.nomeEtiqueta}
                                                    </span>
                                                </label>

                                                {/* ✅ editar só no modo normal */}
                                                {!modoConsultaFiltro && (
                                                    <div className="chat-etiqueta-item-actions">
                                                        <button
                                                            type="button"
                                                            className="chat-etiqueta-icon-btn"
                                                            onClick={() => abrirEdicao(e)}
                                                            disabled={salvandoVinculos}
                                                            title="Editar"
                                                        >
                                                            ✎
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* FOOTER */}
                <div className="chat-etiqueta-modal-footer">
                    {modo === "lista" ? (
                        <>
                            <button
                                type="button"
                                className="chat-etiqueta-btn-secondary"
                                onClick={onFechar}
                                disabled={salvandoVinculos}
                            >
                                Cancelar
                            </button>

                            {/* ✅ footer específico do modo filtro */}
                            {modoConsultaFiltro ? (
                                <>
                                    <button
                                        type="button"
                                        className="chat-etiqueta-btn-secondary"
                                        onClick={limparSelecao}
                                        disabled={salvandoVinculos}
                                        title="Limpar seleção"
                                    >
                                        Limpar seleção
                                    </button>

                                    <button
                                        type="button"
                                        className="chat-etiqueta-btn-primary"
                                        onClick={aplicarFiltro}
                                        disabled={salvandoVinculos}
                                        title="Aplicar filtro de etiqueta"
                                        aria-label="Aplicar filtro de etiqueta"
                                    >
                                        Aplicar filtro de etiqueta
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    className="chat-etiqueta-btn-primary"
                                    onClick={salvarVinculos}
                                    disabled={salvandoVinculos}
                                    aria-label="Confirmar seleção de etiquetas"
                                    title="Confirmar seleção"
                                >
                                    {salvandoVinculos ? "Salvando..." : "Confirmar seleção"}
                                </button>
                            )}
                        </>
                    ) : (
                        <button
                            type="button"
                            className="chat-etiqueta-btn-secondary"
                            onClick={voltarParaLista}
                            disabled={formLoading}
                        >
                            Voltar
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
